(() => {
  const formatWithDelimiters = (cents, precision, thousands, decimal) => {
    const value = (cents / 100).toFixed(precision);
    const [whole, fraction] = value.split('.');
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return fraction ? `${grouped}${decimal}${fraction}` : grouped;
  };

  const formatMoney = (cents, format) => {
    const pattern = /\{\{\s*(\w+)\s*\}\}/;
    const match = format.match(pattern);
    if (!match) return String(cents / 100);
    const formats = {
      amount: () => formatWithDelimiters(cents, 2, ',', '.'),
      amount_no_decimals: () => formatWithDelimiters(cents, 0, ',', '.'),
      amount_with_comma_separator: () => formatWithDelimiters(cents, 2, '.', ','),
      amount_no_decimals_with_comma_separator: () => formatWithDelimiters(cents, 0, '.', ','),
      amount_with_apostrophe_separator: () => formatWithDelimiters(cents, 2, "'", '.'),
    };
    const render = formats[match[1]] || formats.amount;
    return format.replace(pattern, render());
  };

  class MayaGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      this.slides = Array.from(this.querySelectorAll('.maya-gallery__slide'));
      this.thumbs = Array.from(this.querySelectorAll('[data-thumb]'));
      this.dots = Array.from(this.querySelectorAll('.maya-gallery__dot'));
      this.prev = this.querySelector('[data-prev]');
      this.next = this.querySelector('[data-next]');
      this.index = 0;
      if (!this.track || this.slides.length < 2) return;

      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => this.goTo(Number(thumb.dataset.thumb)));
      });
      this.prev?.addEventListener('click', () => this.goTo(this.index - 1));
      this.next?.addEventListener('click', () => this.goTo(this.index + 1));
      this.track.addEventListener('scroll', () => this.onScroll(), { passive: true });
      this.track.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') this.goTo(this.index + 1);
        if (event.key === 'ArrowLeft') this.goTo(this.index - 1);
      });
      this.sync();
    }

    goTo(target) {
      const index = Math.max(0, Math.min(this.slides.length - 1, target));
      this.track.scrollTo({ left: this.slides[index].offsetLeft - this.slides[0].offsetLeft, behavior: 'smooth' });
      this.index = index;
      this.sync();
    }

    goToMedia(mediaId) {
      const index = this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
      if (index >= 0) this.goTo(index);
    }

    onScroll() {
      window.cancelAnimationFrame(this.frame);
      this.frame = window.requestAnimationFrame(() => {
        const step = this.slides[1].offsetLeft - this.slides[0].offsetLeft || this.track.clientWidth;
        const index = Math.min(this.slides.length - 1, Math.round(this.track.scrollLeft / step));
        if (index !== this.index) {
          this.index = index;
          this.sync();
        }
      });
    }

    sync() {
      this.thumbs.forEach((thumb, i) => {
        const active = i === this.index;
        thumb.classList.toggle('is-active', active);
        if (active) thumb.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
      });
      this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === this.index));
      if (this.prev) this.prev.disabled = this.index === 0;
      if (this.next) this.next.disabled = this.index === this.slides.length - 1;
    }
  }

  class MayaPdp extends HTMLElement {
    connectedCallback() {
      const json = this.querySelector('[data-product-json]');
      this.product = json ? JSON.parse(json.textContent) : null;
      this.moneyFormat = this.dataset.moneyFormat || 'R$ {{amount_with_comma_separator}}';
      this.gallery = this.querySelector('maya-gallery');
      this.variantInput = this.querySelector('[data-variant-input]');
      this.qtyInput = this.querySelector('[data-qty-input]');
      this.qtyDisplay = this.querySelector('[data-qty-display]');
      this.cta = this.querySelector('[data-cta]');
      this.ctaLabel = this.querySelector('[data-cta-label]');
      this.ctaPrice = this.querySelector('[data-cta-price]');
      this.tiers = Array.from(this.querySelectorAll('[data-tier-qty]'));
      this.sticky = this.querySelector('[data-sticky]');
      this.ctaText = this.ctaLabel ? this.ctaLabel.textContent.trim() : '';
      this.variant = this.findVariantById(Number(this.variantInput?.value));
      this.quantity = Number(this.qtyInput?.value) || 1;

      this.querySelectorAll('[data-qty-step]').forEach((button) => {
        button.addEventListener('click', () => this.setQuantity(this.quantity + Number(button.dataset.qtyStep)));
      });
      this.tiers.forEach((tier) => {
        tier.addEventListener('click', () => this.setQuantity(Number(tier.dataset.tierQty)));
      });
      this.querySelectorAll('.maya-buy__option input').forEach((input) => {
        input.addEventListener('change', () => this.onOptionChange());
      });
      this.querySelector('[data-buy-now]')?.addEventListener('click', (event) => this.buyNow(event.currentTarget));
      this.setupUgc();
      this.setupSticky();
      this.markUnavailable();
      this.render();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.footerObserver?.disconnect();
    }

    findVariantById(id) {
      return this.product?.variants.find((variant) => variant.id === id) || this.product?.variants[0];
    }

    selectedOptions() {
      return Array.from(this.querySelectorAll('.maya-buy__option')).map((fieldset) => {
        const checked = fieldset.querySelector('input:checked');
        return checked ? checked.value : null;
      });
    }

    onOptionChange() {
      const options = this.selectedOptions();
      const variant = this.product?.variants.find((item) => item.options.every((value, i) => value === options[i]));
      this.querySelectorAll('.maya-buy__option').forEach((fieldset, i) => {
        const label = fieldset.querySelector('[data-option-value]');
        if (label) label.textContent = options[i] || '';
      });
      if (!variant) {
        this.variant = null;
        this.render();
        return;
      }
      this.variant = variant;
      this.variantInput.value = variant.id;
      if (variant.featured_media) this.gallery?.goToMedia(variant.featured_media.id);
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
      this.markUnavailable();
      this.render();
    }

    markUnavailable() {
      if (!this.product) return;
      const options = this.selectedOptions();
      this.querySelectorAll('.maya-buy__option').forEach((fieldset, index) => {
        fieldset.querySelectorAll('.maya-buy__value').forEach((label) => {
          const value = label.querySelector('input').value;
          const candidate = options.map((option, i) => (i === index ? value : option));
          const available = this.product.variants.some(
            (variant) => variant.available && variant.options.every((opt, i) => opt === candidate[i])
          );
          label.classList.toggle('is-unavailable', !available);
        });
      });
    }

    setQuantity(value) {
      this.quantity = Math.max(1, Math.min(99, value));
      if (this.qtyInput) this.qtyInput.value = this.quantity;
      if (this.qtyDisplay) this.qtyDisplay.textContent = this.quantity;
      this.render();
    }

    activeTier() {
      return this.tiers.find((tier) => Number(tier.dataset.tierQty) === this.quantity);
    }

    render() {
      const variant = this.variant;
      const available = Boolean(variant && variant.available);
      const unit = variant ? variant.price : 0;
      const compare = variant ? variant.compare_at_price || 0 : 0;
      const tier = this.activeTier();
      const discount = tier ? Number(tier.dataset.tierDiscount) : 0;
      const total = Math.round((unit * this.quantity * (100 - discount)) / 100);

      const reference = Math.max(unit, compare);
      this.tiers.forEach((item) => {
        const active = item === tier;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-checked', String(active));
        const qty = Number(item.dataset.tierQty);
        const disc = Number(item.dataset.tierDiscount);
        const price = Math.round((unit * qty * (100 - disc)) / 100);
        const full = reference * qty;
        this.setText(item, '[data-tier-price]', formatMoney(price, this.moneyFormat));
        this.setText(item, '[data-tier-each]', formatMoney(Math.round(price / qty), this.moneyFormat));
        const fullNode = item.querySelector('[data-tier-full]');
        if (fullNode) {
          fullNode.hidden = full <= price;
          fullNode.textContent = formatMoney(full, this.moneyFormat);
        }
      });

      const onSale = compare > unit;
      this.toggle('[data-compare]', onSale, formatMoney(compare, this.moneyFormat));
      this.toggle('[data-pct]', onSale, onSale ? `-${Math.round(((compare - unit) * 100) / compare)}%` : '');
      const saving = reference * this.quantity - total;
      this.toggle('[data-eco]', saving > 0);
      const savingNode = this.querySelector('[data-saving]');
      if (savingNode && saving > 0) savingNode.textContent = formatMoney(saving, this.moneyFormat);

      const priceNode = this.querySelector('[data-price]');
      if (priceNode) priceNode.textContent = formatMoney(unit, this.moneyFormat);
      if (this.ctaPrice) this.ctaPrice.textContent = formatMoney(total, this.moneyFormat);
      const stickyPrice = this.querySelector('[data-sticky-price]');
      if (stickyPrice) stickyPrice.textContent = formatMoney(total, this.moneyFormat);

      if (this.cta) {
        this.cta.disabled = !available;
        if (this.ctaLabel) {
          this.ctaLabel.textContent = available
            ? this.ctaText
            : variant
              ? window.variantStrings?.soldOut || 'Esgotado'
              : window.variantStrings?.unavailable || 'Indisponível';
        }
      }
      if (this.variantInput) this.variantInput.disabled = !available;
      this.renderModal(total);
    }

    setText(root, selector, text) {
      const node = root.querySelector(selector);
      if (node) node.textContent = text;
    }

    toggle(selector, visible, text) {
      const node = this.querySelector(selector);
      if (!node) return;
      node.hidden = !visible;
      if (visible && text !== undefined) node.textContent = text;
    }

    async buyNow(button) {
      if (!this.variant || !this.variant.available) return;
      button.disabled = true;
      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: this.variant.id, quantity: this.quantity }] }),
        });
        if (!response.ok) throw new Error(`cart/add ${response.status}`);
        window.location.href = '/checkout';
      } catch (error) {
        console.error('Maya buy now failed', error);
        button.disabled = false;
        this.cta?.click();
      }
    }

    setupUgc() {
      this.modal = this.querySelector('[data-ugc-modal]');
      if (!this.modal) return;
      this.modalSlot = this.modal.querySelector('[data-ugc-slot]');
      this.modalTiers = this.modal.querySelector('[data-ugc-tiers]');
      this.modalCta = this.modal.querySelector('[data-ugc-cta]');
      this.querySelectorAll('[data-ugc-open]').forEach((card) => {
        card.addEventListener('click', () => this.openVideo(card));
        card.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          this.openVideo(card);
        });
      });
      this.modal.querySelector('[data-ugc-close]')?.addEventListener('click', () => this.modal.close());
      this.modal.addEventListener('click', (event) => {
        if (event.target === this.modal) this.modal.close();
      });
      this.modal.addEventListener('close', () => this.closeVideo());
      this.modal.querySelector('[data-ugc-more]')?.addEventListener('click', () => {
        this.modal.close();
        this.querySelector('.maya-buy')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
      this.modalCta?.addEventListener('click', () => {
        if (!this.cta || this.cta.disabled) return;
        this.modal.close();
        this.cta.click();
      });
      this.buildModalTiers();
    }

    buildModalTiers() {
      if (!this.modalTiers) return;
      this.modalTiers.hidden = this.tiers.length === 0;
      this.modalTierButtons = this.tiers.map((tier) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'maya-vmodal__tier';
        button.setAttribute('role', 'radio');
        button.dataset.qty = tier.dataset.tierQty;
        const title = tier.querySelector('.maya-buy__offer-title');
        const label = title ? title.cloneNode(true) : document.createElement('span');
        label.className = 'maya-vmodal__tier-label';
        if (!title) label.textContent = tier.dataset.tierQty;
        const price = document.createElement('span');
        price.className = 'maya-vmodal__tier-price';
        button.append(label, price);
        button.addEventListener('click', () => this.setQuantity(Number(tier.dataset.tierQty)));
        this.modalTiers.append(button);
        return button;
      });
    }

    renderModal(total) {
      if (!this.modal) return;
      const priceNode = this.modal.querySelector('[data-ugc-cta-price]');
      if (priceNode) priceNode.textContent = formatMoney(total, this.moneyFormat);
      if (this.modalCta) this.modalCta.disabled = Boolean(this.cta?.disabled);
      (this.modalTierButtons || []).forEach((button, i) => {
        const tier = this.tiers[i];
        const active = tier.classList.contains('is-active');
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-checked', String(active));
        const source = tier.querySelector('[data-tier-price]');
        const target = button.querySelector('.maya-vmodal__tier-price');
        if (source && target) target.textContent = source.textContent;
      });
    }

    openVideo(card) {
      const source = card.querySelector('video');
      if (!source || !this.modal) return;
      this.closeVideo();
      const video = document.createElement('video');
      video.playsInline = true;
      video.controls = true;
      video.loop = true;
      video.preload = 'auto';
      if (source.poster) video.poster = source.poster;
      source.querySelectorAll('source').forEach((node) => video.append(node.cloneNode()));
      const loading = () => this.modalSlot.classList.add('is-loading');
      const ready = () => this.modalSlot.classList.remove('is-loading');
      video.addEventListener('waiting', loading);
      video.addEventListener('playing', ready);
      video.addEventListener('pause', ready);
      video.addEventListener('error', ready);
      this.modalSlot.append(video);
      this.modal.querySelector('[data-ugc-slot-tag]').textContent = card.dataset.ugcTag || '';
      this.modal.querySelector('[data-ugc-slot-author]').textContent = card.dataset.ugcAuthor || '';
      this.render();
      this.modal.showModal();
      document.documentElement.classList.add('maya-vmodal-open');
      loading();
      video.play().catch(() => {
        video.muted = true;
        video.play().catch(ready);
      });
    }

    closeVideo() {
      if (!this.modalSlot) return;
      this.modalSlot.querySelectorAll('video').forEach((video) => {
        video.pause();
        video.removeAttribute('src');
        video.querySelectorAll('source').forEach((node) => node.remove());
        video.load();
        video.remove();
      });
      this.modalSlot.classList.remove('is-loading');
      document.documentElement.classList.remove('maya-vmodal-open');
    }

    setupSticky() {
      if (!this.sticky || !this.cta) return;
      this.sticky.querySelector('[data-sticky-add]')?.addEventListener('click', () => {
        this.cta.scrollIntoView({ behavior: 'smooth', block: 'center' });
        this.cta.click();
      });
      this.observer = new IntersectionObserver(
        ([entry]) => {
          const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          this.sticky.classList.toggle('is-visible', show);
          this.sticky.setAttribute('aria-hidden', String(!show));
          this.sticky.querySelector('[data-sticky-add]').tabIndex = show ? 0 : -1;
        },
        { threshold: 0 }
      );
      this.observer.observe(this.cta);
      const footer = document.querySelector('.maya-footer, .footer');
      if (footer) {
        this.footerObserver = new IntersectionObserver(([entry]) => {
          this.sticky.classList.toggle('is-docked', entry.isIntersecting);
        });
        this.footerObserver.observe(footer);
      }
    }
  }

  if (!customElements.get('maya-gallery')) customElements.define('maya-gallery', MayaGallery);
  if (!customElements.get('maya-pdp')) customElements.define('maya-pdp', MayaPdp);
})();
